/**
 * ScoringTab Component
 *
 * Investment scoring interface with modal-based score selection:
 * - Displays selected investment scores grouped by category
 * - "Add Score" button opens modal to select category and scores
 * - Supports override editing and score removal
 */

import {
  useInvestmentScores,
  useAvailableScores,
  useAddScore,
  useReplaceCategoryScore,
  useRemoveScore,
  useUpdateInvestmentScoreOverride,
} from '@medsuite/api-client';
import { Spinner, Alert, Button } from '@medsuite/ui-components';
import { useState } from 'react';

import AddScoreModal from './AddScoreModal';

import type { Investment, InvestmentScore } from '@medsuite/models';

interface ScoringTabProps {
  investment: Investment;
  onUpdate?: () => void;
}

export default function ScoringTab({ investment, onUpdate }: ScoringTabProps) {
  const investmentId = investment.investment_id;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<number | null>(null);
  const [overrideValue, setOverrideValue] = useState<number>(0);

  // Fetch data
  const {
    data: investmentScores,
    isLoading: scoresLoading,
    error: scoresError,
  } = useInvestmentScores(investmentId);

  const {
    data: availableScores,
    isLoading: availableLoading,
    error: availableError,
  } = useAvailableScores(investmentId);

  // Mutations
  const addScoreMutation = useAddScore(investmentId);
  const replaceCategoryMutation = useReplaceCategoryScore(investmentId);
  const removeScoreMutation = useRemoveScore(investmentId);
  const updateOverrideMutation = useUpdateInvestmentScoreOverride(investmentId);

  const isLoading = scoresLoading || availableLoading;
  const error = scoresError || availableError;

  // Get all selected score IDs
  const selectedScoreIds =
    investmentScores?.categories?.flatMap(
      (c) => c.investment_scores?.map((s) => s.score_id) || []
    ) || [];

  // Handle adding a single score
  const handleAddScore = async (scoreId: number) => {
    await addScoreMutation.mutateAsync(scoreId);
    onUpdate?.();
  };

  // Handle replacing a score in a category
  const handleReplaceScore = async (categoryId: number, scoreId: number) => {
    await replaceCategoryMutation.mutateAsync({
      scoreCategoryId: categoryId,
      scoreId,
    });
    onUpdate?.();
  };

  // Handle score removal
  const handleRemoveScore = async (investmentScoreId: number) => {
    try {
      await removeScoreMutation.mutateAsync(investmentScoreId);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to remove score:', err);
    }
  };

  // Handle override update
  const handleUpdateOverride = async (investmentScoreId: number) => {
    try {
      await updateOverrideMutation.mutateAsync({
        investmentScoreId,
        scoreOverride: overrideValue,
      });
      setEditingOverride(null);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to update override:', err);
    }
  };

  // Get selection type for a category
  const getSelectionType = (categoryId: number): 'S' | 'M' => {
    const category = availableScores?.categories?.find((c) => c.score_category_id === categoryId);
    return category?.selection_type || 'S';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load scoring data: {error.message}</Alert>;
  }

  // Get categories with scores
  const categoriesWithScores =
    investmentScores?.categories?.filter(
      (c) => c.investment_scores && c.investment_scores.length > 0
    ) || [];

  // Count total selected scores
  const totalSelectedScores = categoriesWithScores.reduce(
    (sum, c) => sum + (c.investment_scores?.length || 0),
    0
  );

  return (
    <div className="space-y-6">
      {/* Composite Score Summary */}
      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-lg border border-indigo-200 dark:border-indigo-700 p-6">
        <div className="text-center">
          <div className="flex justify-center items-baseline gap-8 mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                Composite Score
              </h3>
              <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400">
                {(investmentScores?.composite_score?.total_score ?? 0).toFixed(1)}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                Total Points
              </h3>
              <div className="text-5xl font-bold text-emerald-600 dark:text-emerald-400">
                {investmentScores?.summary?.dimension_totals?.points || 0}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-5 gap-3 text-center max-w-md mx-auto">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Security</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {investmentScores?.summary?.dimension_totals?.security_score || 0}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Growth</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {investmentScores?.summary?.dimension_totals?.growth_score || 0}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Efficiency</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {investmentScores?.summary?.dimension_totals?.efficiency_score || 0}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Infra</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {investmentScores?.summary?.dimension_totals?.infrastructure_score || 0}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Clinical</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {investmentScores?.summary?.dimension_totals?.clinical_score || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Scores Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Selected Scores ({totalSelectedScores})
          </h4>
          <Button variant="primary" size="sm" onClick={() => setIsModalOpen(true)}>
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Score
          </Button>
        </div>

        {/* No scores message */}
        {totalSelectedScores === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              No scores selected
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding scores to this investment.
            </p>
            <div className="mt-4">
              <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Score
              </Button>
            </div>
          </div>
        )}

        {/* Scores grouped by category */}
        {categoriesWithScores.map((category) => (
          <div
            key={category.score_category_id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            {/* Category Header */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {category.score_category_name}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      getSelectionType(category.score_category_id) === 'S'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                    }`}
                  >
                    {getSelectionType(category.score_category_id) === 'S' ? 'Single' : 'Multiple'}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {category.investment_scores?.length || 0} score
                  {(category.investment_scores?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Score List */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {category.investment_scores?.map((invScore: InvestmentScore) => (
                <div
                  key={invScore.investment_score_id}
                  className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Score Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {invScore.score_name}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-sm font-semibold">
                          {invScore.points} pts
                        </span>
                      </div>
                      <div className="mt-1 grid grid-cols-5 gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <div>
                          Sec:{' '}
                          <span className="font-medium text-gray-900 dark:text-gray-200">
                            {invScore.security_score}
                          </span>
                        </div>
                        <div>
                          Grw:{' '}
                          <span className="font-medium text-gray-900 dark:text-gray-200">
                            {invScore.growth_score}
                          </span>
                        </div>
                        <div>
                          Eff:{' '}
                          <span className="font-medium text-gray-900 dark:text-gray-200">
                            {invScore.efficiency_score}
                          </span>
                        </div>
                        <div>
                          Inf:{' '}
                          <span className="font-medium text-gray-900 dark:text-gray-200">
                            {invScore.infrastructure_score}
                          </span>
                        </div>
                        <div>
                          Cln:{' '}
                          <span className="font-medium text-gray-900 dark:text-gray-200">
                            {invScore.clinical_score}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Override Editor */}
                      {editingOverride === invScore.investment_score_id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={overrideValue}
                            onChange={(e) => setOverrideValue(Number(e.target.value))}
                            className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          />
                          <button
                            onClick={() => handleUpdateOverride(invScore.investment_score_id)}
                            disabled={updateOverrideMutation.isPending}
                            className="p-1 text-green-600 hover:text-green-700 dark:text-green-400"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => setEditingOverride(null)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingOverride(invScore.investment_score_id);
                            setOverrideValue(invScore.score_override || 0);
                          }}
                          className="text-xs px-2 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded"
                          title="Edit Override"
                        >
                          Override: {invScore.score_override || 0}
                        </button>
                      )}

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveScore(invScore.investment_score_id)}
                        disabled={removeScoreMutation.isPending}
                        className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Remove"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add Score Modal */}
      <AddScoreModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        categories={availableScores?.categories || []}
        selectedScoreIds={selectedScoreIds}
        investmentScoreCategories={investmentScores?.categories || []}
        onAddScore={handleAddScore}
        onReplaceScore={handleReplaceScore}
        onRemoveScore={handleRemoveScore}
        isLoading={availableLoading}
      />
    </div>
  );
}
